import type { Logger, LogMetadata } from '@genially/logger-lib';
import {
  createConsoleLogger,
  createMultiLogger,
  createRemoteLogger,
} from '@genially/logger-lib';
import config from '@genially/ui/build/config/config';

import { getDefaultLoggerApp } from '../config/env';

export enum LoggerLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3,
}

const appendPrefix = (
  message: any,
  prefix: string = 'P2PClientLib',
  level: string,
): any => {
  if (typeof message === 'string') {
    return `[${prefix} - ${level}] ${message}`;
  }

  return message;
};

export const createLogger = ({
  minLoggerLevel = LoggerLevel.Warn,
  app,
  prefix,
  defaultMetadata,
  customLoggers,
}: {
  minLoggerLevel?: LoggerLevel;
  prefix?: string;
  app?: string;
  defaultMetadata?: LogMetadata;
  customLoggers?: Logger[];
}) => {
  const defaultApp = app ?? getDefaultLoggerApp();

  let loggers: Logger[];

  if (customLoggers) {
    loggers = customLoggers;
  } else {
    loggers = [createConsoleLogger({ app: defaultApp })];

    if (config.appUrls.loggerServer !== undefined) {
      loggers.push(
        createRemoteLogger({ app: defaultApp, remoteHost: config.appUrls.loggerServer }),
      );
    }
  }

  const multiLogger = createMultiLogger(loggers);

  if (defaultMetadata) {
    multiLogger.setDefaultMetadata(defaultMetadata);
  }

  return {
    debug: (message: any, metadata?: LogMetadata) => {
      if (minLoggerLevel <= LoggerLevel.Debug) {
        multiLogger.debug(appendPrefix(message, prefix, 'debug'), metadata);
      }
    },
    info: (message: any, metadata?: LogMetadata) => {
      if (minLoggerLevel <= LoggerLevel.Info) {
        multiLogger.info(appendPrefix(message, prefix, 'info'), metadata);
      }
    },
    warn: (message: any, metadata?: LogMetadata) => {
      if (minLoggerLevel <= LoggerLevel.Warn) {
        multiLogger.warn(appendPrefix(message, prefix, 'warn'), metadata);
      }
    },
    error: (message: any, metadata?: LogMetadata) => {
      if (minLoggerLevel <= LoggerLevel.Error) {
        multiLogger.error(appendPrefix(message, prefix, 'error'), metadata);
      }
    },

    setMetadata: (newMetadata: Record<string, any>) => {
      multiLogger.setDefaultMetadata(newMetadata);
    },
  };
};

export const createOnlyRemoteLogger = (options: {
  minLoggerLevel?: LoggerLevel;
  prefix?: string;
  app?: string;
  defaultMetadata?: LogMetadata;
}) => {
  const defaultApp = options?.app ?? getDefaultLoggerApp();

  let logger: Logger;

  if (config.appUrls.loggerServer !== undefined) {
    logger = createRemoteLogger({
      app: defaultApp,
      remoteHost: config.appUrls.loggerServer,
    });
  } else {
    logger = createConsoleLogger({ app: defaultApp });
  }

  return createLogger({ ...options, customLoggers: [logger] });
};

export type P2pLogger = ReturnType<typeof createLogger>;
